import AlertCircleIcon from "@uui/AlertCircle";
import PlusCircleIcon from "@uui/PlusCircle";
import { find, get, isEmpty, trim } from "lodash/fp";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";

import { Collapse, Input, Typography } from "@mui/material";

import type {
  CustomVocabulary,
  CustomVocabularyType,
  PostCustomVocabularyBody,
} from "@app/analytics/types";
import { yupResolver } from "@app/utils/forms/yupResolver";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import type { DropdownItem } from "@rui/Dropdown/types";
import IconBackground from "@rui/IconBackground";
import Modal, { ModalProps } from "@rui/Modal";
import Stack, { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  CUSTOM_VOCABULARY_TYPE_CONFIG,
  CUSTOM_VOCABULARY_TYPES,
} from "./constants";
import { AddVocabularyInputRow, TypeSelectorLabel } from "./styles";

type VocabInput = { value: string; type: string };

const PERSON_NAME_TOOLTIP =
  "Note: Adding a person's name to custom vocabulary is best suited to people who are often mentioned but are not actually in the meeting. Meeting participant names are automatically boosted by Read.";

interface AddCustomVocabularyModalProps extends Omit<ModalProps, "onClose"> {
  allVocabulary: CustomVocabulary[];
  inWorkspace: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (data: PostCustomVocabularyBody[], onSuccess: () => void) => void;
}

const MAX_WORD_LENGTH = 30;
const WORDS_FIELD_ARRAY_NAME = "words";
const EMPTY_INPUT: VocabInput = { value: "", type: "" };

const VOCAB_VALIDATION_SCHEMA = yup.object().shape({
  vocab: yup.array().of(
    yup.object().shape({
      value: yup.string().required(),
      type: yup.string().required(),
    })
  ),
});

const renderSelectedType = (
  selectedType: DropdownItem<CustomVocabularyType>
) => {
  const { Icon, label } = CUSTOM_VOCABULARY_TYPE_CONFIG[selectedType.value];

  return (
    <>
      <Icon sx={{ fontSize: "14px", color: "shades.neutral.300" }} />
      <Typography
        variant="labelReg"
        sx={{
          color: "shades.neutral.500",
          fontFamily: (theme) => theme.typography.fontFamilySecondary,
        }}
      >
        {label}
      </Typography>
      {selectedType.value === "person_name" && (
        <TooltipWrapper title={PERSON_NAME_TOOLTIP} placement="bottom-end">
          <IconBackground height={20} sx={{ bgcolor: "shades.orange.50" }}>
            <AlertCircleIcon sx={{ color: "#B25102 !important" }} />
          </IconBackground>
        </TooltipWrapper>
      )}
    </>
  );
};

const AddCustomVocabularyModal = ({
  allVocabulary,
  inWorkspace,
  isSaving,
  open,
  onClose,
  onSave,
}: AddCustomVocabularyModalProps) => {
  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(VOCAB_VALIDATION_SCHEMA),
    defaultValues: {
      [WORDS_FIELD_ARRAY_NAME]: [EMPTY_INPUT],
    },
  });

  const fieldArray = useFieldArray({
    control: form.control,
    name: WORDS_FIELD_ARRAY_NAME,
  });

  const handleAddMore = () => fieldArray.append(EMPTY_INPUT);
  const handleClose = () => {
    onClose();
    form.reset();
  };

  const validateUnique = (fields: VocabInput[]) => {
    let hasError = false;

    fields.forEach((field, i) => {
      // ignore any fields without a value populated, since we'll be stripping those out on save anyway
      if (field.value) {
        const wordAlreadyExists = find(
          (v) => v.type === field.type && v.word === trim(field.value),
          allVocabulary
        );
        if (wordAlreadyExists) {
          form.setError(`${WORDS_FIELD_ARRAY_NAME}.${i}.type`, {
            type: "custom",
            message: `This word is already in ${
              inWorkspace ? "your Workspace's" : "your"
            } custom vocabulary`,
          });
          hasError = true;
        }
      }
    });

    return !hasError;
  };

  const validateRequiredFields = (fields: VocabInput[]) => {
    let hasError = false;

    fields.forEach((field, i) => {
      // ignore any fields without a value populated, since we'll be stripping those out on save anyway
      if (field.value && !field.type) {
        form.setError(`${WORDS_FIELD_ARRAY_NAME}.${i}.type`, {
          type: "custom",
          message: "Please specify the type",
        });
        hasError = true;
      }
    });

    return !hasError;
  };

  const validateForm = () => {
    const fields = form.getValues(WORDS_FIELD_ARRAY_NAME);

    return [validateRequiredFields, validateUnique]
      .map((validator) => validator(fields))
      .every((isValid) => isValid);
  };

  const handleSubmit = () => {
    const isValid = validateForm();
    if (!isValid) return;

    const newWords = form
      .getValues(WORDS_FIELD_ARRAY_NAME)
      // filter out any empty inputs
      .filter(({ value }) => Boolean(value))
      .map((field) => ({
        word: field.value,
        type: field.type as CustomVocabularyType,
      }));

    if (isEmpty(newWords)) {
      handleClose();
    } else {
      onSave(newWords, form.reset);
    }
  };

  const handleSave = form.handleSubmit(handleSubmit);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack spacing={2}>
        <Typography variant="h2" color="shades.neutral.700">
          Add custom vocabulary
        </Typography>
        {inWorkspace && (
          <Alert embedded severity="info">
            Custom vocabulary is shared across your Workspace
          </Alert>
        )}
        <Stack spacing="2px">
          <Stack spacing={1} sx={{ maxHeight: "160px", overflowY: "auto" }}>
            {fieldArray.fields.map((field, i) => {
              const valueFieldName =
                `${WORDS_FIELD_ARRAY_NAME}.${i}.value` as const;
              const typeFieldName =
                `${WORDS_FIELD_ARRAY_NAME}.${i}.type` as const;

              const registeredValueField = form.register(valueFieldName);

              const selectedType = form.watch(
                typeFieldName
              ) as CustomVocabularyType;
              const selectedItem = CUSTOM_VOCABULARY_TYPE_CONFIG[selectedType];
              const errorMessage = get(
                `${typeFieldName}.message`,
                form.formState.errors
              );

              const hasError = Boolean(errorMessage);

              return (
                <Stack key={valueFieldName} spacing="2px">
                  <AddVocabularyInputRow error={hasError}>
                    <Row flexGrow={1} variant="left">
                      <Input
                        disableUnderline
                        fullWidth
                        placeholder="Type your word here..."
                        inputProps={{
                          ...registeredValueField,
                          maxLength: MAX_WORD_LENGTH,
                        }}
                      />
                    </Row>
                    <Row flexShrink={0} variant="left">
                      <Dropdown
                        variant="text"
                        name={typeFieldName}
                        placeholder="Select type"
                        sx={{ minHeight: "unset" }}
                        selectedItem={selectedItem}
                        onChange={(event) => {
                          form.clearErrors(typeFieldName);
                          form.setValue(typeFieldName, event.target.value);
                        }}
                        renderSelectedItem={renderSelectedType}
                      >
                        {CUSTOM_VOCABULARY_TYPES.map((config) => {
                          const isSelected = config.value === selectedType;
                          return (
                            <DropdownMenuItem
                              key={config.value}
                              label={
                                <TypeSelectorLabel
                                  config={config}
                                  selected={isSelected}
                                />
                              }
                              selected={isSelected}
                              value={config.value}
                            />
                          );
                        })}
                      </Dropdown>
                    </Row>
                  </AddVocabularyInputRow>
                  <Collapse in={hasError} mountOnEnter>
                    <Row variant="left" marginLeft="4px">
                      <Typography
                        variant="labelMed"
                        color="error.main"
                        sx={{
                          fontFamily: (theme) =>
                            theme.typography.fontFamilySecondary,
                        }}
                      >
                        {errorMessage}
                      </Typography>
                    </Row>
                  </Collapse>
                </Stack>
              );
            })}
          </Stack>
          <Row>
            <Button
              variant="text"
              startIcon={<PlusCircleIcon />}
              onClick={handleAddMore}
            >
              Add more
            </Button>
          </Row>
        </Stack>
        <Row variant="right" spacing={2}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            sx={{ minWidth: "100px" }}
            onClick={handleSave}
            loading={isSaving}
          >
            Save
          </Button>
        </Row>
      </Stack>
    </Modal>
  );
};

export default AddCustomVocabularyModal;
